import { translate as t } from "./locale";
import { useState } from "react";

import { isTrainerShareUrl, TRAINER_SHARE_IMPORT_MESSAGE } from "./project-import";
import { trackTelemetryAction } from "./telemetry";

export const submitProjectImportLink = async (
    url: string,
    currentOrigin: string,
    onLoadLink: (url: string) => Promise<boolean>,
): Promise<{ error: string; loaded: boolean }> => {
    if (isTrainerShareUrl(url, currentOrigin))
        return { error: TRAINER_SHARE_IMPORT_MESSAGE, loaded: false };
    return { error: "", loaded: await onLoadLink(url) };
};

export const ProjectImportDialog = ({
    onCancel,
    onChooseFile,
    onLoadLink,
}: {
    onCancel: () => void;
    onChooseFile: () => void;
    onLoadLink: (url: string) => Promise<boolean>;
}) => {
    const [linkMode, setLinkMode] = useState(false);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState("");

    const submitLink = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const entry = form.get("scratch-project-url");
        const url = typeof entry === "string" ? entry.trim() : "";
        if (!url) {
            setError(t("Wklej link do publicznego projektu Scratch."));
            return;
        }
        setWorking(true);
        setError("");
        try {
            const result = await submitProjectImportLink(url, window.location.origin, onLoadLink);
            if (result.error) {
                setError(result.error);
                setWorking(false);
                trackTelemetryAction("project_import", {
                    reason: "trainer_share_link",
                    result: "rejected",
                    source: "link",
                });
                return;
            }
            if (!result.loaded) {
                setWorking(false);
                return;
            }
            trackTelemetryAction("project_import", { result: "success", source: "link" });
        } catch (caught) {
            trackTelemetryAction("project_import", { result: "failure", source: "link" });
            setError(
                caught instanceof Error && caught.message !== "INVALID_SCRATCH_PROJECT_URL"
                    ? caught.message
                    : t("Wklej prawidłowy link https://scratch.mit.edu/projects/…"),
            );
            setWorking(false);
        }
    };

    return (
        <div className="modal-overlay" role="presentation">
            <section
                aria-labelledby="import-dialog-title"
                aria-modal="true"
                className="import-dialog"
                role="dialog"
            >
                {!linkMode ? (
                    <div className="import-dialog-content">
                        <h2 id="import-dialog-title">{t("Wczytaj projekt")}</h2>
                        <p>{t("Wybierz źródło projektu.")}</p>
                        <div className="dialog-actions import-source-actions">
                            <button onClick={onChooseFile} type="button">
                                {t("Wczytaj z dysku (.sb3)")}
                            </button>
                            <button onClick={() => setLinkMode(true)} type="button">
                                {t("Wczytaj link")}
                            </button>
                            <button className="secondary-button" onClick={onCancel} type="button">
                                {t("Anuluj")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="import-dialog-content" onSubmit={(event) => void submitLink(event)}>
                        <h2 id="import-dialog-title">{t("Wczytaj projekt z linku")}</h2>
                        <p>
                            {t("Wklej publiczny link do projektu Scratch, np. https://scratch.mit.edu/projects/551985594")}</p>
                        <label>
                            <span>{t("Link Scratch:")}</span>
                            <input
                                autoComplete="off"
                                disabled={working}
                                inputMode="url"
                                name="scratch-project-url"
                                placeholder="https://scratch.mit.edu/projects/551985594"
                                spellCheck={false}
                                type="url"
                            />
                        </label>
                        {error && <p className="form-error" role="alert">{error}</p>}
                        <div className="dialog-actions">
                            <button disabled={working} type="submit">
                                {working ? t("Wczytywanie…") : t("Wczytaj")}
                            </button>
                            <button
                                className="secondary-button"
                                disabled={working}
                                onClick={onCancel}
                                type="button"
                            >
                                {t("Anuluj")}
                            </button>
                        </div>
                    </form>
                )}
            </section>
        </div>
    );
};
